/**
 * Zoho CRM Integration Factory
 * Creates and configures Zoho CRM integration instances
 */

import { ZohoCRMIntegration, ZohoCRMConfig } from './ZohoCRMIntegration';
import { IntegrationConfig, ValidationResult } from '../../../types/integration';
import { BaseIntegration } from '../base/BaseIntegration';
import { IntegrationFactory } from '../registry/IntegrationRegistry';

export class ZohoCRMIntegrationFactory implements IntegrationFactory {
  /**
   * Create a new Zoho CRM integration instance (IntegrationFactory interface)
   */
  async create(config: IntegrationConfig): Promise<BaseIntegration> {
    return ZohoCRMIntegrationFactory.create(config);
  }

  /**
   * Validate configuration (IntegrationFactory interface)
   */
  async validate(config: Partial<IntegrationConfig>): Promise<ValidationResult> {
    // Convert to ZohoCRMConfig for validation
    const zohoConfig = config as Partial<ZohoCRMConfig>;
    const validation = ZohoCRMIntegrationFactory.validateConfig(zohoConfig as ZohoCRMConfig);
    
    return {
      valid: validation.valid,
      errors: validation.errors.map(error => ({
        field: 'config',
        message: error,
        code: 'VALIDATION_ERROR'
      })),
      warnings: []
    };
  }

  /**
   * Get configuration schema (IntegrationFactory interface)
   */
  getConfigSchema(): Record<string, any> {
    return {
      type: 'object',
      required: ['clientId', 'clientSecret', 'redirectUri', 'dataCenter'],
      properties: {
        clientId: {
          type: 'string',
          title: 'Client ID',
          description: 'Your Zoho CRM OAuth client ID',
          minLength: 10,
          maxLength: 100
        },
        clientSecret: {
          type: 'string',
          title: 'Client Secret',
          description: 'Your Zoho CRM OAuth client secret',
          minLength: 10,
          maxLength: 100
        },
        redirectUri: {
          type: 'string',
          title: 'Redirect URI',
          description: 'OAuth redirect URI configured in your Zoho app',
          format: 'uri',
          pattern: '^https?://.+'
        },
        dataCenter: {
          type: 'string',
          title: 'Data Center',
          description: 'Your Zoho data center location',
          enum: ['com', 'eu', 'in', 'com.au', 'jp'],
          enumNames: ['United States', 'Europe', 'India', 'Australia', 'Japan']
        },
        scopes: {
          type: 'array',
          title: 'OAuth Scopes',
          description: 'Permissions requested from Zoho CRM',
          items: {
            type: 'string',
            enum: [
              'ZohoCRM.modules.ALL',
              'ZohoCRM.modules.Contacts.ALL',
              'ZohoCRM.modules.Leads.ALL',
              'ZohoCRM.modules.Accounts.ALL',
              'ZohoCRM.modules.Deals.ALL',
              'ZohoCRM.modules.Activities.ALL',
              'ZohoCRM.settings.ALL',
              'ZohoCRM.users.READ',
              'ZohoCRM.org.READ',
              'ZohoCRM.bulk.READ',
              'ZohoCRM.bulk.WRITE',
              'ZohoCRM.notifications.ALL'
            ]
          },
          default: [
            'ZohoCRM.modules.Contacts.ALL',
            'ZohoCRM.modules.Leads.ALL',
            'ZohoCRM.modules.Activities.ALL',
            'ZohoCRM.users.READ'
          ]
        }
      },
      additionalProperties: false
    };
  }

  /**
   * Get supported features (IntegrationFactory interface)
   */
  getSupportedFeatures(): string[] {
    return [
      'bidirectional_sync',
      'real_time_sync',
      'webhook_support',
      'batch_operations',
      'custom_fields',
      'oauth2_authentication',
      'contact_sync',
      'lead_sync',
      'activity_logging',
      'conflict_resolution',
      'field_mapping',
      'rate_limiting'
    ];
  }

  /**
   * Create a new Zoho CRM integration instance (static method)
   */
  static create(config: IntegrationConfig | ZohoCRMConfig): ZohoCRMIntegration {
    // Convert IntegrationConfig to ZohoCRMConfig if needed
    let zohoConfig: ZohoCRMConfig;
    
    if ('clientId' in config) {
      // Already a ZohoCRMConfig
      zohoConfig = config as ZohoCRMConfig;
    } else {
      // Convert from IntegrationConfig
      const baseConfig = config as IntegrationConfig;
      zohoConfig = {
        id: baseConfig.id,
        name: baseConfig.name,
        type: baseConfig.type,
        platform: baseConfig.platform || 'zoho',
        status: baseConfig.status,
        clientId: '', // Will be set from credentials
        clientSecret: '', // Will be set from credentials
        redirectUri: '', // Will be set from credentials
        dataCenter: 'com', // Default
        scopes: [
          'ZohoCRM.modules.Contacts.ALL',
          'ZohoCRM.modules.Leads.ALL',
          'ZohoCRM.users.READ'
        ],
        credentials: baseConfig.credentials,
        settings: baseConfig.settings,
        fieldMappings: baseConfig.fieldMappings,
        syncSchedule: baseConfig.syncSchedule,
        webhookConfig: baseConfig.webhookConfig,
        createdAt: baseConfig.createdAt,
        updatedAt: baseConfig.updatedAt,
        lastSyncAt: baseConfig.lastSyncAt,
        healthStatus: baseConfig.healthStatus,
        metadata: baseConfig.metadata
      };
    }
    
    // Validate required Zoho-specific configuration
    if (!zohoConfig.clientId) {
      throw new Error('Zoho CRM client ID is required');
    }
    
    if (!zohoConfig.clientSecret) {
      throw new Error('Zoho CRM client secret is required');
    }

    if (!zohoConfig.redirectUri) {
      throw new Error('Zoho CRM redirect URI is required');
    }

    if (!zohoConfig.dataCenter) {
      throw new Error('Zoho CRM data center is required');
    }

    // Set default configuration values
    const defaultConfig: Partial<ZohoCRMConfig> = {
      scopes: zohoConfig.scopes || [
        'ZohoCRM.modules.ALL',
        'ZohoCRM.settings.ALL',
        'ZohoCRM.users.READ',
        'ZohoCRM.org.READ'
      ],
      settings: {
        enabled: true,
        autoSync: true,
        batchSize: 200, // Zoho supports up to 200 records per batch
        rateLimitPerMinute: 200,
        retryAttempts: 3,
        timeoutMs: 30000,
        customSettings: {
          syncFrequency: 'hourly',
          retryDelay: 1000,
          webhookRetryAttempts: 5
        },
        ...zohoConfig.settings
      },
      fieldMappings: zohoConfig.fieldMappings.length > 0 ? zohoConfig.fieldMappings : [
        {
          id: 'email_mapping',
          sourceField: 'email',
          targetField: 'Email',
          direction: 'bidirectional',
          required: true,
          dataType: 'string'
        },
        {
          id: 'firstName_mapping',
          sourceField: 'firstName',
          targetField: 'First_Name',
          direction: 'bidirectional',
          required: false,
          dataType: 'string'
        },
        {
          id: 'lastName_mapping',
          sourceField: 'lastName',
          targetField: 'Last_Name',
          direction: 'bidirectional',
          required: false,
          dataType: 'string'
        },
        {
          id: 'phone_mapping',
          sourceField: 'phone',
          targetField: 'Phone',
          direction: 'bidirectional',
          required: false,
          dataType: 'string'
        },
        {
          id: 'company_mapping',
          sourceField: 'company',
          targetField: 'Account_Name.name',
          direction: 'bidirectional',
          required: false,
          dataType: 'string'
        }
      ]
    };

    // Merge default config with provided config
    const finalConfig: ZohoCRMConfig = {
      ...defaultConfig,
      ...zohoConfig,
      settings: {
        ...defaultConfig.settings,
        ...zohoConfig.settings
      },
      scopes: zohoConfig.scopes || defaultConfig.scopes!
    };

    return new ZohoCRMIntegration(finalConfig);
  }

  /**
   * Create a Zoho CRM integration with OAuth credentials
   */
  static createWithOAuth(
    clientId: string,
    clientSecret: string,
    redirectUri: string,
    dataCenter: 'com' | 'eu' | 'in' | 'com.au' | 'jp' = 'com',
    options: Partial<ZohoCRMConfig> = {}
  ): ZohoCRMIntegration {
    const config: ZohoCRMConfig = {
      id: options.id || `zoho_${Date.now()}`,
      name: options.name || 'Zoho CRM Integration',
      type: 'crm',
      platform: 'zoho',
      status: 'inactive',
      clientId,
      clientSecret,
      redirectUri,
      dataCenter,
      scopes: options.scopes || [
        'ZohoCRM.modules.ALL',
        'ZohoCRM.settings.ALL',
        'ZohoCRM.users.READ',
        'ZohoCRM.org.READ'
      ],
      credentials: {
        encrypted: JSON.stringify({
          clientId,
          clientSecret,
          redirectUri,
          dataCenter
        }),
        keyId: 'default',
        algorithm: 'AES-256-GCM',
        createdAt: new Date()
      },
      settings: {
        enabled: true,
        autoSync: true,
        batchSize: 200,
        rateLimitPerMinute: 200,
        retryAttempts: 3,
        timeoutMs: 30000,
        customSettings: {
          syncFrequency: 'hourly',
          retryDelay: 1000,
          webhookRetryAttempts: 5,
          ...options.settings
        }
      },
      fieldMappings: options.fieldMappings || [],
      syncSchedule: options.syncSchedule || {
        enabled: true,
        frequency: 'hourly',
        timezone: 'UTC',
        batchSize: 200,
        conflictResolution: 'manual'
      },
      createdAt: new Date(),
      updatedAt: new Date(),
      healthStatus: {
        status: 'healthy',
        lastCheck: new Date(),
        responseTime: 0,
        errorRate: 0,
        uptime: 100,
        issues: []
      },
      metadata: {
        description: 'Zoho CRM integration for email marketing platform',
        tags: ['zoho', 'crm', 'integration'],
        category: 'CRM',
        version: '1.0.0',
        supportedFeatures: ['contacts', 'leads', 'oauth2', 'webhooks'],
        limitations: [],
        customData: {}
      },
      ...options
    };

    return this.create(config);
  }

  /**
   * Validate Zoho CRM configuration
   */
  static validateConfig(config: ZohoCRMConfig): { valid: boolean; errors: string[] } {
    const errors: string[] = [];

    // Required fields validation
    if (!config.clientId || config.clientId.trim() === '') {
      errors.push('Client ID is required');
    }

    if (!config.clientSecret || config.clientSecret.trim() === '') {
      errors.push('Client secret is required');
    }

    if (!config.redirectUri || config.redirectUri.trim() === '') {
      errors.push('Redirect URI is required');
    }

    if (!config.dataCenter) {
      errors.push('Data center is required');
    }

    // Data center validation
    const validDataCenters = ['com', 'eu', 'in', 'com.au', 'jp'];
    if (config.dataCenter && !validDataCenters.includes(config.dataCenter)) {
      errors.push(`Invalid data center. Must be one of: ${validDataCenters.join(', ')}`);
    }

    // Redirect URI format validation
    if (config.redirectUri && !config.redirectUri.match(/^https?:\/\/.+/)) {
      errors.push('Redirect URI must be a valid HTTP/HTTPS URL');
    }

    // Scopes validation
    if (config.scopes && config.scopes.length === 0) {
      errors.push('At least one scope is required');
    }

    const validScopePatterns = [
      /^ZohoCRM\.modules\.(ALL|[A-Z][a-zA-Z_]*\.(ALL|READ|WRITE|CREATE|UPDATE|DELETE))$/,
      /^ZohoCRM\.settings\.(ALL|READ|WRITE)$/,
      /^ZohoCRM\.users\.(ALL|READ|WRITE)$/,
      /^ZohoCRM\.org\.(ALL|READ|WRITE)$/,
      /^ZohoCRM\.bulk\.(ALL|READ|WRITE)$/,
      /^ZohoCRM\.notifications\.(ALL|READ|WRITE)$/
    ];

    if (config.scopes) {
      config.scopes.forEach((scope, index) => {
        const isValidScope = validScopePatterns.some(pattern => pattern.test(scope));
        if (!isValidScope) {
          errors.push(`Invalid scope at position ${index + 1}: ${scope}`);
        }
      });
    }

    // Field mappings validation
    if (config.fieldMappings) {
      config.fieldMappings.forEach((mapping, index) => {
        if (!mapping.sourceField || !mapping.targetField) {
          errors.push(`Field mapping ${index + 1} is missing source or target field`);
        }
        
        if (!['bidirectional', 'to_platform', 'from_platform'].includes(mapping.direction)) {
          errors.push(`Field mapping ${index + 1} has invalid direction`);
        }
      });
    }

    // Settings validation
    if (config.settings) {
      if (config.settings.batchSize && (config.settings.batchSize < 1 || config.settings.batchSize > 200)) {
        errors.push('Batch size must be between 1 and 200 for Zoho CRM');
      }

      if (config.settings.retryAttempts && (config.settings.retryAttempts < 0 || config.settings.retryAttempts > 10)) {
        errors.push('Retry attempts must be between 0 and 10');
      }

      if (config.settings.customSettings?.retryDelay && config.settings.customSettings.retryDelay < 100) {
        errors.push('Retry delay must be at least 100ms');
      }
    }

    return {
      valid: errors.length === 0,
      errors
    };
  }

  /**
   * Get default field mappings for Zoho CRM
   */
  static getDefaultFieldMappings() {
    return [
      {
        sourceField: 'email',
        targetField: 'Email',
        direction: 'bidirectional' as const,
        required: true,
        dataType: 'string' as const,
        description: 'Primary email address'
      },
      {
        sourceField: 'firstName',
        targetField: 'First_Name',
        direction: 'bidirectional' as const,
        required: false,
        dataType: 'string' as const,
        description: 'First name'
      },
      {
        sourceField: 'lastName',
        targetField: 'Last_Name',
        direction: 'bidirectional' as const,
        required: false,
        dataType: 'string' as const,
        description: 'Last name'
      },
      {
        sourceField: 'phone',
        targetField: 'Phone',
        direction: 'bidirectional' as const,
        required: false,
        dataType: 'string' as const,
        description: 'Primary phone number'
      },
      {
        sourceField: 'mobilePhone',
        targetField: 'Mobile',
        direction: 'bidirectional' as const,
        required: false,
        dataType: 'string' as const,
        description: 'Mobile phone number'
      },
      {
        sourceField: 'company',
        targetField: 'Account_Name.name',
        direction: 'bidirectional' as const,
        required: false,
        dataType: 'string' as const,
        description: 'Company/Account name'
      },
      {
        sourceField: 'leadSource',
        targetField: 'Lead_Source',
        direction: 'bidirectional' as const,
        required: false,
        dataType: 'string' as const,
        description: 'Lead source'
      }
    ];
  }

  /**
   * Get OAuth authorization URL for Zoho CRM
   */
  static getAuthorizationUrl(
    clientId: string,
    redirectUri: string,
    dataCenter: string = 'com',
    scopes: string[] = ['ZohoCRM.modules.ALL'],
    state?: string
  ): string {
    const baseUrl = `https://accounts.zoho.${dataCenter}/oauth/v2/auth`;
    const params = new URLSearchParams({
      response_type: 'code',
      client_id: clientId,
      scope: scopes.join(','),
      redirect_uri: redirectUri,
      access_type: 'offline'
    });

    if (state) {
      params.append('state', state);
    }

    return `${baseUrl}?${params.toString()}`;
  }

  /**
   * Get available Zoho data centers
   */
  static getAvailableDataCenters() {
    return [
      { code: 'com', name: 'United States', domain: 'zoho.com' },
      { code: 'eu', name: 'Europe', domain: 'zoho.eu' },
      { code: 'in', name: 'India', domain: 'zoho.in' },
      { code: 'com.au', name: 'Australia', domain: 'zoho.com.au' },
      { code: 'jp', name: 'Japan', domain: 'zoho.jp' }
    ];
  }

  /**
   * Get available OAuth scopes for Zoho CRM
   */
  static getAvailableScopes() {
    return [
      {
        scope: 'ZohoCRM.modules.ALL',
        description: 'Full access to all CRM modules',
        category: 'modules'
      },
      {
        scope: 'ZohoCRM.modules.Contacts.ALL',
        description: 'Full access to Contacts module',
        category: 'modules'
      },
      {
        scope: 'ZohoCRM.modules.Leads.ALL',
        description: 'Full access to Leads module',
        category: 'modules'
      },
      {
        scope: 'ZohoCRM.modules.Accounts.ALL',
        description: 'Full access to Accounts module',
        category: 'modules'
      },
      {
        scope: 'ZohoCRM.modules.Deals.ALL',
        description: 'Full access to Deals module',
        category: 'modules'
      },
      {
        scope: 'ZohoCRM.modules.Activities.ALL',
        description: 'Full access to Activities module',
        category: 'modules'
      },
      {
        scope: 'ZohoCRM.settings.ALL',
        description: 'Access to CRM settings and metadata',
        category: 'settings'
      },
      {
        scope: 'ZohoCRM.users.READ',
        description: 'Read access to user information',
        category: 'users'
      },
      {
        scope: 'ZohoCRM.org.READ',
        description: 'Read access to organization information',
        category: 'organization'
      },
      {
        scope: 'ZohoCRM.bulk.READ',
        description: 'Access to bulk read operations',
        category: 'bulk'
      },
      {
        scope: 'ZohoCRM.bulk.WRITE',
        description: 'Access to bulk write operations',
        category: 'bulk'
      },
      {
        scope: 'ZohoCRM.notifications.ALL',
        description: 'Access to notification settings',
        category: 'notifications'
      }
    ];
  }

  /**
   * Create test configuration for development/testing
   */
  static createTestConfig(): ZohoCRMConfig {
    return {
      id: 'zoho_test',
      name: 'Zoho CRM Test Integration',
      type: 'crm',
      platform: 'zoho',
      status: 'inactive',
      clientId: 'test_client_id_1234567890',
      clientSecret: 'test_client_secret_abcdef1234567890',
      redirectUri: 'https://localhost:3000/auth/zoho/callback',
      dataCenter: 'com',
      scopes: [
        'ZohoCRM.modules.Contacts.ALL',
        'ZohoCRM.modules.Leads.ALL',
        'ZohoCRM.users.READ'
      ],
      credentials: {
        encrypted: JSON.stringify({
          clientId: 'test_client_id_1234567890',
          clientSecret: 'test_client_secret_abcdef1234567890',
          redirectUri: 'https://localhost:3000/auth/zoho/callback',
          dataCenter: 'com'
        }),
        keyId: 'test_key',
        algorithm: 'AES-256-GCM',
        createdAt: new Date()
      },
      settings: {
        enabled: false,
        autoSync: false,
        batchSize: 10,
        rateLimitPerMinute: 60,
        retryAttempts: 1,
        timeoutMs: 5000,
        customSettings: {
          syncFrequency: 'manual',
          retryDelay: 500,
          webhookRetryAttempts: 2
        }
      },
      fieldMappings: this.getDefaultFieldMappings().map(mapping => ({
        ...mapping,
        id: `test_${mapping.sourceField}_mapping`
      })),
      syncSchedule: {
        enabled: false,
        frequency: 'manual',
        timezone: 'UTC',
        batchSize: 10,
        conflictResolution: 'manual'
      },
      createdAt: new Date(),
      updatedAt: new Date(),
      healthStatus: {
        status: 'unhealthy',
        lastCheck: new Date(),
        responseTime: 0,
        errorRate: 0,
        uptime: 0,
        issues: []
      },
      metadata: {
        description: 'Test Zoho CRM integration for development',
        tags: ['test', 'zoho', 'crm'],
        category: 'CRM',
        version: '1.0.0',
        supportedFeatures: ['contacts', 'leads', 'oauth2'],
        limitations: ['test environment only'],
        customData: {}
      }
    };
  }

  /**
   * Validate OAuth callback parameters
   */
  static validateOAuthCallback(params: { code?: string; error?: string; state?: string }): {
    valid: boolean;
    error?: string;
    code?: string;
    state?: string;
  } {
    if (params.error) {
      return {
        valid: false,
        error: `OAuth error: ${params.error}`
      };
    }

    if (!params.code) {
      return {
        valid: false,
        error: 'Authorization code is missing'
      };
    }

    return {
      valid: true,
      code: params.code,
      state: params.state
    };
  }
}

// Export singleton instance
export const zohoCRMIntegrationFactory = new ZohoCRMIntegrationFactory();